import type { CarePlan } from '@lmc/ts-common'
import { Card } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { ListItem } from 'components/generic'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { mixpanel } from '../../../../common/services/analytics'
import { NAMESPACE } from '../../../../locale'
import { riskAssessmentsConstants } from '../../../../locale/riskAssessments'
import RiskAssessmentItem from '../../../../screens/Residents/screens/ResidentRiskAssessmentsScreens/components/RiskAssessmentItem'

type AssessmentItem = {
  id: string
  form?: {
    id?: string
    name: string
    iconText?: string
    color?: string
  }
  name?: string
  iconText?: string
  color?: string
  riskLevel?: string
  riskScore?: number
  incomplete?: boolean
  createdOn?: string
  reviewedOn?: string
  createdBy?: string
  carePlans?: Array<CarePlan>
}

interface SimilarAssessmentsProps {
  similarAssessments?: Array<AssessmentItem>
  carers?: Array<{
    id: string
    name: {
      first: string
      last: string
    }
  }>
  onDelete?: (riskAssessmentId: string) => void
  carePlanNominative: string
  router?: {
    navigate: (path: string) => void
    params: { id?: string }
  }
  user?: {
    id: string
  }
  home?: {
    id: string
    name: string
  }
  addExisting: (riskAssessment: AssessmentItem, callback: () => void) => void
  addNew: (
    riskAssessmentId: string,
    isCustomTemplate: boolean,
    callback: () => void
  ) => void
  goToNextStep: (() => void) | undefined
}

const SimilarAssessments: React.FC<SimilarAssessmentsProps> = ({
  similarAssessments = [],
  carers = [],
  onDelete,
  carePlanNominative,
  router,
  user,
  home,
  addExisting,
  addNew,
  goToNextStep
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
  const getCarer = (createdBy: string) => {
    return carers.find((carer) => carer.id === createdBy)
  }

  const isExisting = (item: AssessmentItem) => item.form && item.form.id

  const handleRiskAssessmentClick = (riskAssessment: AssessmentItem) => {
    if (router && router.params.id) {
      const residentId = router.params.id

      mixpanel.track('similar_risk_assessment_selected', {
        platform: 'Care Office',
        distinct_id: user?.id,
        home: home?.id,
        homeName: home?.name,
        residentId
      })

      if (!goToNextStep) return

      if (isExisting(riskAssessment)) {
        addExisting(riskAssessment, goToNextStep)
      } else {
        addNew(riskAssessment.id, false, goToNextStep)
      }
    }
  }

  return (
    <Card>
      <StyledContainer>
        <StyledTitle level={1} weight="bold">
          {t(riskAssessmentsConstants.SIMILAR_ASSESSMENTS_TITLE)}
        </StyledTitle>
        <StyledSubtitle level={2} weight="regular">
          {t(riskAssessmentsConstants.SIMILAR_ASSESSMENTS_SUBTITLE)}
        </StyledSubtitle>
        <StyledListContainer>
          {similarAssessments.map((riskAssessment) => {
            if (isExisting(riskAssessment)) {
              if (!riskAssessment.createdBy) return null
              const carer = getCarer(riskAssessment.createdBy)
              if (!carer) return null
              return (
                <StyledWrapper key={riskAssessment.id}>
                  <RiskAssessmentItem
                    riskAssessment={riskAssessment}
                    carer={carer}
                    onEdit={() => handleRiskAssessmentClick(riskAssessment)}
                    onView={() => handleRiskAssessmentClick(riskAssessment)}
                    onDelete={onDelete}
                    carePlanNominative={carePlanNominative}
                    allowRedirect={false}
                  />
                </StyledWrapper>
              )
            }

            return (
              <StyledWrapper key={riskAssessment.id}>
                <ListItem
                  iconText={riskAssessment.iconText}
                  iconColor={riskAssessment.color}
                  label={riskAssessment.name}
                  onSelect={() => handleRiskAssessmentClick(riskAssessment)}
                  subLabel="No reviews yet"
                  iconSize={20}
                  size={40}
                />
              </StyledWrapper>
            )
          })}
        </StyledListContainer>
      </StyledContainer>
    </Card>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledTitle = styled(Text)`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledSubtitle = styled(Text)`
  margin-bottom: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledWrapper = styled.div`
  .lmc-list-item {
    background-color: ${({ theme }) => theme.color.neutral.color10};
    border: 0 !important;
  }
`

export default SimilarAssessments
